import { useMemo } from 'react';

import { SearchUtils } from '@spotify/confidence-core-react';

import { useCurrentIdentity } from '../useCurrentIdentity';
import { useCurrentUserGroups } from '../useCurrentUserGroups';

type UseCurrentUserAndGroupsFilterOptions = {
  field: string;
  skip?: boolean;
};

type UseCurrentUserAndGroupsFilterResult = {
  filter: string | undefined;
  identityNames: string[];
  isLoading: boolean;
  error: Error | null | undefined;
};

export const useCurrentUserAndGroupsFilter = ({
  field,
  skip = false,
}: UseCurrentUserAndGroupsFilterOptions): UseCurrentUserAndGroupsFilterResult => {
  const {
    currentIdentity,
    isLoading: isLoadingCurrentIdentity,
    error: identityError,
  } = useCurrentIdentity({ skip });
  const {
    identities: currentUserGroupIdentities,
    isLoading: isLoadingCurrentUserGroups,
    error: groupsError,
    hasUnavailableGroups,
  } = useCurrentUserGroups({ skip });
  const identityNames = useMemo(
    () => [
      ...new Set(
        [
          currentIdentity?.name,
          ...currentUserGroupIdentities.map(identity => identity.name),
        ].filter((name): name is string => Boolean(name)),
      ),
    ],
    [currentIdentity?.name, currentUserGroupIdentities],
  );

  const error =
    identityError ??
    groupsError ??
    (!skip && hasUnavailableGroups
      ? new Error('Could not load group memberships')
      : undefined) ??
    (!skip && !isLoadingCurrentIdentity && !currentIdentity
      ? new Error('Could not load current identity')
      : undefined);

  if (skip || identityNames.length === 0) {
    return {
      filter: undefined,
      error,
      identityNames,
      isLoading:
        !skip && (isLoadingCurrentIdentity || isLoadingCurrentUserGroups),
    };
  }

  return {
    filter: SearchUtils.buildAnyOfFilter(field, identityNames),
    error,
    identityNames,
    isLoading: isLoadingCurrentIdentity || isLoadingCurrentUserGroups,
  };
};
